/**
 * Phone numbers leave the core in the same shape the Stencil widget sends:
 * libphonenumber's international format, `+91 76954 67223`, whatever the
 * visitor typed (text-widget/utils getPhoneNumberWithInternationalCode).
 *
 * The anti-spam layer at the edge in front of the widget service refuses an
 * unformatted number with a 494 whose body reads like an outage ("We're
 * having trouble processing your request"), so a visitor who typed a local
 * number would be told to try again later for something they could fix in a
 * second. The same shape as the standard widget keeps the two indistinguishable
 * to that layer. It also refuses well-formed numbers that are not real,
 * assigned ones (2026-09-29: `+91 7695467223` parsed as valid and was still
 * refused; a real number went through). Only the pattern can be checked here.
 *
 * The library is the build the standard widget loads from our CDN, fetched
 * once on the first submit with a phone field and read off `window` — not a
 * dependency of this package, so the core bundle stays small and a page that
 * already runs the standard widget shares one copy (text-widget.tsx
 * loadPhoneLib). If it cannot load, the number goes as typed, which is what
 * the standard widget does too.
 */

import { hostWindow } from '../host/globals';
import { warn, logError } from '../log';

/** The standard widget's copy. text-widget.tsx imports this, so there is one version. */
export const PHONE_LIB_URL = 'https://stcdn.leadconnectorhq.com/libphonenumber/1.12.41/libphonenumber-js.min.js';

/** The slice of libphonenumber-js the core uses. */
export interface PhoneLib {
  parsePhoneNumberFromString(
    text: string,
    defaultCountry?: string,
  ): { isValid(): boolean; formatInternational(): string } | undefined;
}

export type PhoneNormalisation =
  | { ok: true; formatted: string }
  | { ok: false; reason: 'invalid' | 'no_country' | 'unavailable' };

const isCountryCode = (c: string | null | undefined): c is string => !!c && /^[A-Za-z]{2}$/.test(c);

/** ISO-2 region from a widget locale such as `en-us`; `location` / `auto` / `de` give nothing. */
export function countryFromLocale(locale: string | undefined | null): string | null {
  const region = (locale || '').split(/[-_]/)[1];
  return isCountryCode(region) ? region.toUpperCase() : null;
}

function loadedPhoneLib(): PhoneLib | null {
  const lib = hostWindow()?.libphonenumber;
  return lib && typeof lib.parsePhoneNumberFromString === 'function' ? lib : null;
}

let loading: Promise<PhoneLib | null> | null = null;

/**
 * Drop the memoised load so the next call starts over. For specs, which share
 * one module instance across cases; not exported from the package entry, so it
 * is not part of the host-facing API.
 */
export function resetPhoneLibLoader(): void {
  loading = null;
}

/**
 * How long to wait for the CDN before giving up and sending the number as
 * typed. The check is a nicety; blocking a submit on it is not.
 */
export const PHONE_LIB_LOAD_TIMEOUT_MS = 5000;

/**
 * The library, loading it from the CDN the first time. One script tag per
 * page: a tag the standard widget already added is waited on, not repeated.
 *
 * The outcome is memoised for the life of the page — a blocked CDN would
 * otherwise cost every submit the full timeout. A library that arrives late is
 * still used, because loadedPhoneLib() is checked before the memo.
 */
export function loadPhoneLib(): Promise<PhoneLib | null> {
  const loaded = loadedPhoneLib();
  if (loaded) return Promise.resolve(loaded);
  if (loading) return loading;
  const w = hostWindow();
  if (!w || typeof document === 'undefined') return Promise.resolve(null);

  loading = new Promise<PhoneLib | null>(resolve => {
    const present = document.querySelector(`script[src="${PHONE_LIB_URL}"]`) as HTMLScriptElement | null;
    const script = present ?? document.createElement('script');
    // The UMD build registers with an AMD loader instead of `window` when one
    // is present; the standard widget hides `define` for the load, so do we.
    const amd = !!(w.require && w.define && w.define.amd);
    const oldDefine = w.define;
    if (amd) w.define = null;

    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (lib: PhoneLib | null) => {
      // load, error and the timeout all race; whichever lands first wins, and
      // the rest must not restore `define` a second time or resolve again.
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      if (amd) w.define = oldDefine;
      // `loading` is deliberately NOT cleared: it memoises the outcome. A
      // blocked CDN (an ad-blocker, an offline visitor) would otherwise cost
      // every submit the full timeout again, where the standard widget never
      // blocked a submit on this at all. A library that arrives late is still
      // picked up — loadedPhoneLib() is checked before this promise.
      resolve(lib);
    };

    // A tag left by the standard widget (or an earlier client) may already
    // have fired load or error before we got here — listeners attached now
    // would never run, and every submit with a phone field awaits this
    // promise. The timeout is what makes that case terminate: it is the only
    // signal available, since a script element does not report its outcome.
    timer = setTimeout(() => {
      warn(`the phone library did not load within ${PHONE_LIB_LOAD_TIMEOUT_MS}ms; sending numbers as typed`);
      finish(loadedPhoneLib());
    }, PHONE_LIB_LOAD_TIMEOUT_MS);

    script.addEventListener('load', () => finish(loadedPhoneLib()), { once: true });
    script.addEventListener(
      'error',
      () => {
        logError(`the phone library could not be loaded from ${PHONE_LIB_URL}`);
        finish(null);
      },
      { once: true },
    );

    if (!present) {
      script.src = PHONE_LIB_URL;
      script.async = true;
      document.head.appendChild(script);
    }
  });
  return loading;
}

export async function normalisePhone(raw: string, defaultCountry: string | null): Promise<PhoneNormalisation> {
  const trimmed = raw.trim();
  const country = isCountryCode(defaultCountry) ? defaultCountry.toUpperCase() : undefined;
  if (!trimmed.startsWith('+') && !country) return { ok: false, reason: 'no_country' };
  const lib = await loadPhoneLib();
  if (!lib) return { ok: false, reason: 'unavailable' };
  const parsed = lib.parsePhoneNumberFromString(trimmed, country);
  return parsed?.isValid() ? { ok: true, formatted: parsed.formatInternational() } : { ok: false, reason: 'invalid' };
}
